import * as React from "react";
import CommunicationNoteDialog, {
    CommunicationNotesHistoryProps,
} from "../CommunicationNoteDialog/CommunicationNoteDialog";
import { CallLogResponse, CallStoreResponse } from "intake_shared/src/types";
import Skeleton from "../../../bits/Skeleton/Skeleton";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import Table from "../../../bits/Table/Table";
import Alert from "../../../bits/Alert/Alert";
import CommunicationPhoneLink, {
    getRingCentralUrl,
} from "../CommunicationPhoneLink/CommunicationPhoneLink";
import DirectionIcon from "../DirectionIcon/DirectionIcon";
import formatCommunicationHistoryTimestamp from "../formatCommunicationHistoryTimestamp";

interface CallHistoryProps extends CommunicationNotesHistoryProps {
    callLogs?: CallStoreResponse;
    callLoading: boolean;
    callError: boolean;
    clientPhone: string;
}

function convertDuration(durationInSeconds) {
    if (durationInSeconds >= 60) {
        // Convert to minutes
        return (durationInSeconds / 60).toFixed(2) + " minutes";
    } else {
        // Keep in seconds
        return durationInSeconds + " seconds";
    }
}

export default function CallHistory({
    callLogs,
    callLoading,
    callError,
    clientPhone,
    notes = [],
    noteActions,
}: CallHistoryProps) {
    const notesByRecord = React.useMemo(() => {
        const grouped = new Map<string, typeof notes>();
        for (const note of notes) {
            if (note.channel !== "call") continue;
            const recordNotes = grouped.get(note.ringcentral_record_id) ?? [];
            recordNotes.push(note);
            grouped.set(note.ringcentral_record_id, recordNotes);
        }
        return grouped;
    }, [notes]);
    const columns = React.useMemo(() => {
        return [
            {
                Header: "",
                id: "On",
                accessor: (row: CallLogResponse) => (
                    <p>
                        <DirectionIcon direction={row?.direction} />
                        {formatCommunicationHistoryTimestamp(row?.startTime)}
                    </p>
                ),
            },

            {
                Header: "From",
                accessor: (row: CallLogResponse) => (
                    <CommunicationPhoneLink
                        value={row?.from?.phoneNumber || row?.from?.name}
                    />
                ),
            },
            {
                Header: "To",
                accessor: (row: CallLogResponse) => (
                    <CommunicationPhoneLink
                        value={row?.to?.phoneNumber || row?.to?.extensionNumber}
                    />
                ),
            },

            {
                Header: "Duration",
                accessor: (row: CallLogResponse) => (
                    <p>{convertDuration(row?.duration)}</p>
                ),
            },
            {
                Header: "Action",
                accessor: (row: CallLogResponse) => <p>{row?.action}</p>,
            },
            {
                Header: "Type",
                accessor: (row: CallLogResponse) => <p>{row?.type}</p>,
            },
            {
                Header: "Result",
                accessor: (row: CallLogResponse) => <p>{row?.result}</p>,
            },
            {
                Header: "Notes",
                id: "notes",
                accessor: (row: CallLogResponse) =>
                    row.id && noteActions ? (
                        <CommunicationNoteDialog
                            key={row.id}
                            channel="call"
                            recordId={String(row.id)}
                            notes={notesByRecord.get(String(row.id)) ?? []}
                            actions={noteActions}
                        />
                    ) : null,
            },
        ];
    }, [noteActions, notesByRecord]);

    const callHistoryUrl = getRingCentralUrl(clientPhone, "call-history");

    return (
        <div>
            {callLoading ? (
                <Skeleton count={3} />
            ) : callError ? (
                <Alert type="warning">
                    Call history could not be loaded. Please try again.
                </Alert>
            ) : !callLogs || callLogs?.records?.length === 0 ? (
                <EmptyListState
                    emptyMessage="No Call History Yet"
                    stateHelperText="There are no call logs to display. Call logs will appear here once communication begins."
                />
            ) : (
                <>
                    {callLogs.truncated ? (
                        <Alert type="warning">
                            Showing the latest 500 call records. Open RingCentral to
                            view the complete history.
                        </Alert>
                    ) : null}
                    <Table
                        columns={columns}
                        data={callLogs.records}
                        allowSelect={false}
                        handleRowClick={
                            callHistoryUrl
                                ? () =>
                                      window.open(
                                          callHistoryUrl,
                                          "_blank",
                                          "noopener,noreferrer"
                                      )
                                : undefined
                        }
                    />
                </>
            )}
        </div>
    );
}
